import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useRef } from 'react';

import { SPAN_UPDATE_ENERGY_TARIFF } from '@constants/otel';
import { EnergyQuoteType } from '@generated/gql/graphql';
import { useSelectEnergy } from '@hooks/useEnergySelection';
import { useSyncedAction } from '@hooks/useSyncedAction';
import { RequestID } from '@hooks/useSyncedRequest';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useTrace } from '@services/otel';
import { getBundleBasket } from '@utils/queries/getBasket';

const useHandleTariffUpdate = () => {
  const { withTrace } = useTrace();
  const { basketId } = useBasketContext();
  const { isSyncedAppointment, hasControl } = useAppointmentContext();
  const [refetchBasket, { loading: isRefetching }] =
    useLazyQuery(getBundleBasket);

  const { selectQuotes, loading } = useSelectEnergy();
  const { updateActionLoading, syncedLoading } = useSyncedAction(
    RequestID.UPDATE_ENERGY_TARIFF
  );

  // Passive client doesn't make mutations, so it needs to refetch the basket
  // after active client's mutation completes to get the updated data.
  // We track syncedLoading transitions to know when to trigger refetch.
  const isPassiveClient = isSyncedAppointment && !hasControl;
  const prevSyncedLoading = useRef(syncedLoading);

  useEffect(() => {
    if (!isPassiveClient) return;

    const wasSyncedLoading = prevSyncedLoading.current;

    // When syncedLoading transitions from true to false, refetch basket
    if (wasSyncedLoading && !syncedLoading) {
      refetchBasket({ variables: { basketId } });
    }

    prevSyncedLoading.current = syncedLoading;
  }, [isPassiveClient, syncedLoading, refetchBasket, basketId]);

  const handleUpdateQuotes = async (
    quoteType: EnergyQuoteType,
    onComplete: () => void
  ) => {
    await withTrace(SPAN_UPDATE_ENERGY_TARIFF, () =>
      updateActionLoading(selectQuotes(basketId, quoteType))
    );
    onComplete();
  };

  // prevSyncedLoading.current bridges the gap between syncedLoading ending and isRefetching starting
  const isLoading = isPassiveClient
    ? syncedLoading || prevSyncedLoading.current || isRefetching
    : loading;

  return {
    handleUpdateQuotes,
    isLoading,
  };
};

export { useHandleTariffUpdate };
